import { Text } from "react-native-paper";
import { View } from "react-native";
import { useState } from "react";
import { capitalize, replace } from "lodash";

import { Employment, RootStackParamList } from "../types";
import EmploymentMenu from "../../components/NavMenu";
import { useNavigation } from "@react-navigation/native";
import RemoveEmploymentModal from "../EmploymentEditScreen/RemoveEmploymentModal";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { EmploymentWarningModal } from "../../components/EmploymentWarningModal";
import InactiveStatusModals from "../../components/InactiveStatusModals";
import { ChangeStatusEnum } from "../EmploymentEditScreen/types";
import { Section } from "../HomeScreen/types";

const isRemote = (workingLocation: Employment["workingLocation"]) => workingLocation === "REMOTE";

const getWorkingLocation = (workingLocation: Employment["workingLocation"]) => {
  if (isRemote(workingLocation)) return "Remote/Regional";
  return replace(capitalize(workingLocation), "_", "-");
};

type EmploymentSectionProps = {
  employment: Employment;
  hasOnlyOneEmployment: boolean;
  primarySection?: Section;
};

const EmploymentSection = ({ employment, hasOnlyOneEmployment, primarySection }: EmploymentSectionProps) => {
  const [isRemoveEmploymentModalVisible, setIsRemoveEmploymentModalVisible] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showStatusChangeModal, setShowStatusChangeModal] = useState<ChangeStatusEnum | "">("");
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  function navigateToSelectPrimaryEmployment() {
    navigation.navigate("SelectPrimaryEmployment", { employmentToRemove: employment.id });
  }

  function navigateToEmploymentEdit() {
    navigation.navigate("EmploymentEdit", { id: employment.id });
  }

  function handleRemoveEmployment() {
    if (employment.isPrimary) {
      navigateToSelectPrimaryEmployment();
    } else {
      navigation.navigate("EmploymentRemove", { id: employment.id });
    }
  }

  return (
    <View className="rounded-lg border border-sky bg-sky-white flex p-6 mt-5">
      <RemoveEmploymentModal
        visible={isRemoveEmploymentModalVisible}
        onDismiss={() => setIsRemoveEmploymentModalVisible(false)}
        navigation={navigation}
        employmentId={employment.id}
        onRemoveAndChangeStatus={() => setShowWarningModal(true)}
        setShowStatusChangeModal={setShowStatusChangeModal as (value: string) => void}
      />
      <EmploymentWarningModal
        visible={showWarningModal}
        onDismiss={() => setShowWarningModal(false)}
        title="Unemployed status"
        text="When unemployed, your section assignment and membership options and benefits could be affected."
        onContinue={() => {
          navigation.navigate("EmploymentRemove", { id: employment.id });
          setShowWarningModal(false);
        }}
      />
      <InactiveStatusModals
        showStatusChangeWarningModal={showStatusChangeModal}
        setShowStatusChangeWarningModal={setShowStatusChangeModal}
        onContinue={() => navigation.navigate("EmploymentRemove", { id: employment.id, nextAction: showStatusChangeModal })}
      />
      <View className="flex-row justify-between items-center">
        <View className="flex-1">
          <Text className="text-small-tight font-400 text-ink-dark">{`${
            employment.isPrimary ? "Primary" : "Secondary"
          } Facility`}</Text>
          <Text className="text-regular-tight font-700 text-ink-darkest mt-1 mr-3" numberOfLines={2} ellipsizeMode="tail">
            {employment.facility.name}
          </Text>
        </View>
        <EmploymentMenu
          anchorPosition="bottom"
          items={[
            { title: "Edit Employment", icon: "edit", onPress: navigateToEmploymentEdit },
            {
              title: "Remove Employment",
              icon: "trash",
              onPress: () => {
                hasOnlyOneEmployment ? setIsRemoveEmploymentModalVisible(true) : handleRemoveEmployment();
              },
            },
          ]}
        />
      </View>

      <View className="flex-col mt-5">
        <Text className="text-small-tight font-400 text-ink-darker">Job Title</Text>
        {employment.title && (
          <Text className="text-regular-tight font-400 text-ink-darkest mt-1">{employment.title ?? "None"}</Text>
        )}
      </View>

      <View className="flex-col mt-5">
        <Text className="text-small-tight font-400 text-ink-darker">Classification</Text>
        <Text className="text-regular-tight font-400 text-ink-darkest mt-1">{employment.jobClassification ?? "None"}</Text>
      </View>

      <View className="flex-col mt-5">
        <Text className="text-small-tight font-400 text-ink-darker">Working Location</Text>
        <Text className="text-regular-tight font-400 text-ink-darkest mt-1">
          {employment.workingLocation ? getWorkingLocation(employment.workingLocation) : "None"}
        </Text>
      </View>

      <View className="flex-col mt-5">
        <Text className="text-small-tight font-400 text-ink-darker">State/Province</Text>
        <Text className="text-regular-tight font-400 text-ink-darkest mt-1">
          {employment.facility.addresses[0]?.state ?? "None"}
        </Text>
      </View>

      <View className="flex-col mt-5">
        <Text className="text-small-tight font-400 text-ink-darker">Section</Text>
        <Text className="text-regular-tight font-400 text-ink-darkest mt-1">
          {employment.isPrimary && isRemote(employment.workingLocation)
            ? primarySection?.name
            : employment.facility.section?.name}
        </Text>
      </View>
    </View>
  );
};

export default EmploymentSection;
